import React, { useState, useEffect } from "react";
import { MoonLoader } from "react-spinners";
import Utils from "../../../utility";
import { sessionManager } from "../../../managers/sessionManager";
import { httpConstants } from "../../../constants";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { getProjectDetails } from "../../../services/userService";

const Editor = () => {
  const { templateId, projectId } = useParams();
  const navigate = useNavigate();
  const [projectDetails, setProjectDetails] = useState(null);
  const brandDetailsFromRedux = useSelector(
    (state) => state?.user?.brandDetails
  );

  const [isEditorLoading, setIsEditorLoading] = useState(true);
  const accessToken = sessionManager.getDataFromCookies(
    httpConstants.HEADERS.AUTH0_ID_TOKEN
  );

  useEffect(() => {
    if (!projectId) return;
    Utils.parseResponse(getProjectDetails(projectId))
      .then(([err, response]) => {
        if (err) {
          console.error("load failed", err);
        } else {
          setProjectDetails(response.responseData);
        }
      })
      .finally(() => setIsEditorLoading(false));
  }, [projectId]);

  // Define the function outside useEffect so it can be reused
  const sendTokenToIframe = () => {
    const iframe = document.querySelector("iframe");
    if (!iframe?.src) {
      console.log("⚠️ No iframe found or iframe src not set");
      return;
    }

    const iframeOrigin = new URL(iframe.src).origin;

    // Get the original image URL
    const originalImageUrl =
      projectDetails?.selectedImage || projectDetails?.createdImage;

    // Prepare image URL for cross-origin access
    const preparedImageUrl = originalImageUrl
      ? Utils.prepareImageUrlForEditor(originalImageUrl)
      : null;

    // For CloudFront images, also pass the original URL since template editor can handle CORS
    const isCloudFrontImage = originalImageUrl?.includes("cloudfront.net");
    const finalImageUrl = isCloudFrontImage
      ? originalImageUrl
      : preparedImageUrl;

    // Ensure generatedData includes the CTA text
    const generatedData = projectDetails?.generatedData || {};

    // Map primaryText to callToAction if callToAction is missing
    if (!generatedData.callToAction && generatedData.primaryText?.length > 0) {
      generatedData.callToAction = generatedData.primaryText[0];
    }

    // Extract brand details from various possible locations
    // Priority: Redux state > project details > empty object
    const brandDetails =
      brandDetailsFromRedux ||
      projectDetails?.brandDetails ||
      projectDetails?.brand ||
      projectDetails?.brandData ||
      {};

    console.log("🔍 Brand details sources:", {
      fromRedux: !!brandDetailsFromRedux,
      fromProject: !!(
        projectDetails?.brandDetails ||
        projectDetails?.brand ||
        projectDetails?.brandData
      ),
      hasLogo: !!(
        brandDetails?.theme?.logo ||
        brandDetails?.logo ||
        brandDetailsFromRedux?.theme?.logo
      ),
      reduxLogo: brandDetailsFromRedux?.theme?.logo,
      projectLogo: projectDetails?.brandDetails?.theme?.logo
    });

    // Ensure logo is accessible from multiple possible locations
    if (!brandDetails.theme && brandDetails.logo) {
      brandDetails.theme = { logo: brandDetails.logo };
    }
    if (!brandDetails.theme?.logo && brandDetailsFromRedux?.theme?.logo) {
      if (!brandDetails.theme) brandDetails.theme = {};
      brandDetails.theme.logo = brandDetailsFromRedux.theme.logo;
    }

    const messageData = {
      accessToken,
      projectDetails: {
        ...projectDetails,
        selectedImage: finalImageUrl,
        generatedData: generatedData,
        brandDetails: brandDetails,
        brand: brandDetails // Include both for compatibility
      }
    };

    console.log("📨 Sending message to iframe:", {
      origin: iframeOrigin,
      hasAccessToken: !!accessToken,
      hasProjectDetails: !!projectDetails,
      originalImageUrl: originalImageUrl || "❌",
      preparedImageUrl: preparedImageUrl || "❌",
      selectedImage: messageData.projectDetails.selectedImage ? "✅" : "❌",
      generatedData: messageData.projectDetails.generatedData ? "✅" : "❌",
      brandDetails: messageData.projectDetails.brandDetails ? "✅" : "❌"
    });

    console.log("🏷️ Brand details being sent:", {
      brandDetails: brandDetails,
      hasLogo: !!brandDetails?.theme?.logo || !!brandDetails?.logo,
      logoUrl: brandDetails?.theme?.logo || brandDetails?.logo,
      brandName: brandDetails?.name || brandDetails?.brandName
    });

    console.log("📝 CTA text being sent:", {
      callToAction: generatedData?.callToAction,
      primaryText: generatedData?.primaryText,
      finalCTA: generatedData?.callToAction || generatedData?.primaryText?.[0]
    });

    console.log("🖼️ Image URL details:", {
      original: originalImageUrl,
      prepared: preparedImageUrl,
      final: finalImageUrl,
      isCloudFront: isCloudFrontImage,
      isAbsolute: finalImageUrl?.startsWith("http"),
      domain: finalImageUrl ? new URL(finalImageUrl).hostname : "no-url"
    });

    // Test image accessibility before sending (skip for CloudFront due to CORS)
    if (finalImageUrl && !isCloudFrontImage) {
      Utils.testImageAccessibility(finalImageUrl).then((accessible) => {
        console.log(
          `🧪 Image accessibility test for ${finalImageUrl}:`,
          accessible ? "✅ PASSED" : "❌ FAILED"
        );
      });
    } else if (isCloudFrontImage) {
      console.log(
        "⚠️ Skipping accessibility test for CloudFront image due to CORS restrictions"
      );
      console.log("💡 Template editor will handle CloudFront CORS internally");
    }

    iframe.contentWindow.postMessage(messageData, iframeOrigin);
  };

  // Define the function to handle iframe load
  const handleIframeLoad = () => {
    setIsEditorLoading(false);
    // Add a small delay to ensure the iframe listener is ready
    setTimeout(sendTokenToIframe, 100);
  };

  useEffect(() => {
    // Listen for messages from the iframe
    const messageHandler = (event) => {
      console.log("🔄 Received message from iframe:", {
        origin: event.origin,
        type: event.data?.type,
        templateEditorUrl: process.env.REACT_APP_TEMPLATE_EDITOR_URL
      });

      // Get origin from template editor URL
      const templateEditorOrigin = new URL(
        process.env.REACT_APP_TEMPLATE_EDITOR_URL
      ).origin;
      // Check if message is from template editor
      if (event.origin === templateEditorOrigin) {
        if (event.data.type === "REQUEST_PROJECT_DATA") {
          console.log("📤 Sending project data to template editor");
          sendTokenToIframe();
        } else if (event.data.type === "NAVIGATE_TO_PROJECT") {
          console.log("🏠 Navigating back to Generated Ads page...");

          // Extract brandId from projectDetails
          const brandId =
            projectDetails?.brandId?._id ||
            projectDetails?.brandId?.id ||
            projectDetails?.brandId ||
            projectDetails?.brandDetails?._id ||
            projectDetails?.brandDetails?.id ||
            brandDetailsFromRedux?._id ||
            brandDetailsFromRedux?.id;

          console.log("📋 Navigation details:", {
            projectId: event.data.projectId,
            brandId: brandId,
            hasBrandId: !!brandId
          });

          if (brandId) {
            navigate(
              `/dashboard/brand/details/${brandId}/${event.data.projectId}`
            );
          } else {
            console.warn("⚠️ No brandId found, using fallback URL");
            navigate(`/welcome/generated-ads/${event.data.projectId}`);
          }
        } else if (event.data.type === "NAVIGATE_TO_FINAL_REVIEW") {
          console.log("✅ Navigating to Final Review page...");
          console.log("📦 Full event.data:", event.data);
          const { projectId, templateId, url } = event.data;
          console.log("📋 Extracted values:", { projectId, templateId, url });
          if (url) {
            console.log("🔗 Navigating to URL:", url);
            navigate(url);
          } else if (projectId && templateId) {
            const finalUrl = `/dashboard/brand/details/project/finalReview/${projectId}/${templateId}`;
            console.log("🔗 Navigating to constructed URL:", finalUrl);
            navigate(finalUrl);
          } else {
            console.error("❌ Missing projectId or templateId for navigation");
          }
        }
      }
      // Legacy support for user webapp messages
      if (event.origin === process.env.REACT_APP_USER_WEBAPP_URL) {
        console.log("📤 Sending project data (legacy support)");
        sendTokenToIframe();
      }
    };

    window.addEventListener("message", messageHandler);

    return () => {
      window.removeEventListener("message", messageHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, projectDetails, navigate]);

  return (
    <div className="flex size-full items-center justify-center">
      <iframe
        className="size-full"
        // src={`http://localhost:5173/${templateId}/${projectId}`}
        src={(() => {
          const baseUrl =
            process.env.REACT_APP_TEMPLATE_EDITOR_URL?.replace(/\/+$/, "") ||
            "";
          const cleanUrl = `${baseUrl}/${templateId}/${projectId}`;
          console.log("🔗 Constructing iframe URL:", {
            baseUrl: process.env.REACT_APP_TEMPLATE_EDITOR_URL,
            cleanedBase: baseUrl,
            templateId,
            projectId,
            finalUrl: cleanUrl
          });
          return cleanUrl;
        })()}
        title="Editor"
        onLoad={handleIframeLoad}
      />
      {isEditorLoading && (
        <MoonLoader
          loading={isEditorLoading}
          color="#17C471"
          cssOverride={{
            position: "absolute",
            zIndex: 1000,
            top: "45%",
            left: "50%"
          }}
        />
      )}
    </div>
  );
};

export default Editor;
